import React from 'react';
import {
  Box, Stack, Button, Tooltip, LinearProgress, Typography, Alert, FormControlLabel, Checkbox
} from '@mui/material';
import { DoneAll, RemoveDone } from '@mui/icons-material';
import BasicTable2 from '../tools/BasicTable2';
import * as ETVConstants from '../ETVConstants';

class FloorDeviceConnection extends React.Component {
  state = {
    floors: [],
    floorsLoaded: false,
    servingFloorMap: {},   // key: String(floor_id) → boolean
    isLoadingFloors: false,
    isLoadingServing: false,
    isBulkUpdating: false,
    error: null,
  };

  componentDidMount() {
    this.loadAllFloors();
    this.loadServingFloors();
  }

  componentDidUpdate(prevProps, prevState) {
    // If we ever need to refetch (e.g., after a parent refresh), we can use a flag.
    if (!this.state.floorsLoaded && !this.state.isLoadingFloors) {
      this.loadAllFloors();
    }
    if (prevProps.device_id !== this.props.device_id || prevProps.location_id !== this.props.location_id) {
      this.loadAllFloors();
      this.loadServingFloors();
    }
  }

  // ---------- helpers ----------
  k = (id) => String(id);

  // ---------- data ----------
  loadAllFloors = () => {
    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    const url = `${ETVConstants.getServerBase()}/floor_access?action=get_floors&location_id=${this.props.location_id}`;
    this.setState({ isLoadingFloors: true, error: null });

    fetch(url, { headers: { 'user_id': reqUserID, 'access_token': accessToken } })
      .then(res => res.json())
      .then(
        (result) => {
          const list = Array.isArray(result.list) ? result.list : [];
          this.setState({
            floors: list,
            floorsLoaded: true,
            isLoadingFloors: false,
          });
        },
        (error) => this.setState({ floors: [], floorsLoaded: false, isLoadingFloors: false, error })
      );
  };

  loadServingFloors = () => {
    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    const url = `${ETVConstants.getServerBase()}/floor_access?action=get_serving_floors&device_id=${encodeURIComponent(
      this.props.device_id
    )}&location_id=${this.props.location_id}`;

    this.setState({ isLoadingServing: true, error: null });

    fetch(url, { headers: { 'user_id': reqUserID, 'access_token': accessToken } })
      .then(res => res.json())
      .then(
        (result) => {
          const map = {};
          const list = Array.isArray(result.list) ? result.list : [];
          list.forEach(item => { map[this.k(item.floor_id)] = true; });
          this.setState({ servingFloorMap: map, isLoadingServing: false });
        },
        (error) => this.setState({ servingFloorMap: {}, isLoadingServing: false, error })
      );
  };

  // ---------- server updates ----------
  setServingFloorRequest = (floorID, isServing) => {
    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    const url = `${ETVConstants.getServerBase()}/floor_access?action=set_serving_floor`
      + `&floor_id=${floorID}`
      + `&device_id=${encodeURIComponent(this.props.device_id)}`
      + `&is_serving=${isServing}`;
    return fetch(url, { headers: { 'user_id': reqUserID, 'access_token': accessToken } }).then(r => r.json());
  };

  setServingFloor = (floorID, isServing) => {
    // Optimistic UI update
    const key = this.k(floorID);
    this.setState(prev => ({
      servingFloorMap: { ...prev.servingFloorMap, [key]: isServing }
    }));
    // Fire-and-forget single update
    this.setServingFloorRequest(floorID, isServing).catch(() => {
      // Revert on failure
      this.setState(prev => ({
        servingFloorMap: { ...prev.servingFloorMap, [key]: !isServing }
      }));
    });
  };

  bulkSet = async (checked) => {
    const { floors } = this.state;
    if (!floors || floors.length === 0) return;
    this.setState({ isBulkUpdating: true });

    // Optimistic map
    const newMap = {};
    floors.forEach(f => { newMap[this.k(f.floor_id)] = checked; });
    this.setState({ servingFloorMap: newMap });

    try {
      // Batch all requests
      await Promise.all(
        floors.map(f => this.setServingFloorRequest(f.floor_id, checked).catch(() => null))
      );
    } finally {
      this.setState({ isBulkUpdating: false });
    }
  };

  // ---------- table ----------
  columnsDataFloor = () => ([
    { title: ETVConstants.trans('floor_label'), field: 'floor_label' },
    {
      title: ETVConstants.trans('floor_info'),
      field: 'floor_info',
      render: (rd) => (
        <Typography variant="body2" color="text.secondary">
          {rd.floor_info?.nodes?.length
            ? `${rd.floor_info.nodes.length} nodes`
            : '-'}
        </Typography>
      )
    },
    {
      title: ETVConstants.trans('serving_floor'),
      field: '',
      render: (rowData) => {
        const key = this.k(rowData.floor_id);
        return (
          <FormControlLabel
            sx={{ m: 0 }}
            control={
              <Checkbox
                checked={!!this.state.servingFloorMap[key]}
                onChange={(e) => this.setServingFloor(rowData.floor_id, e.target.checked)}
              />
            }
            label=""
          />
        );
      }
    }
  ]);

  // ---------- render ----------
  render() {
    const {
      floors, floorsLoaded, isLoadingFloors, isLoadingServing, isBulkUpdating, error, servingFloorMap
    } = this.state;

    const total = floors.length;
    const selected = Object.values(servingFloorMap).filter(Boolean).length;
    const busy = isLoadingFloors || isLoadingServing || isBulkUpdating;

    return (
      <Box sx={{ mt: 1, width: 'min(900px, 100%)' }}>
        {/* Toolbar */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1">{ETVConstants.trans('floors') || 'Floors'}</Typography>
          <Typography variant="body2" color="text.secondary">
            {selected}/{total} {ETVConstants.trans('selected') || 'selected'}
          </Typography>
          <Box flexGrow={1} />
          <Tooltip title={ETVConstants.trans('select_all') || 'Select all'}>
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DoneAll />}
                disabled={!floorsLoaded || total === 0 || busy}
                onClick={() => this.bulkSet(true)}
              >
                {ETVConstants.trans('select_all') || 'Select all'}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={ETVConstants.trans('deselect_all') || 'Deselect all'}>
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<RemoveDone />}
                disabled={!floorsLoaded || total === 0 || busy}
                onClick={() => this.bulkSet(false)}
              >
                {ETVConstants.trans('deselect_all') || 'Deselect all'}
              </Button>
            </span>
          </Tooltip>
        </Stack>

        {busy && <LinearProgress sx={{ mb: 1 }} />}

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {ETVConstants.trans('error') || 'Error'}: {String(error)}
          </Alert>
        ) : (
          <BasicTable2
            data={floors}
            columns={this.columnsDataFloor()}
            title=""
            pageSize={10}
            allow_search
          />
        )}
      </Box>
    );
  }
}

export default FloorDeviceConnection;
